'use client';

import { CaretRight, Hammer, X } from '@phosphor-icons/react/dist/ssr';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import NodeIcon from '@skrym/shared/components/node-icon';
import { Badge } from '@skrym/shared/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@skrym/shared/ui/collapsible';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from '@skrym/shared/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@skrym/shared/ui/tooltip';

import type { PageMetadata } from '@/.velite';

interface AppSidebarNavProps {
  docPages: PageMetadata[];
}

export function AppSidebarNav(props: AppSidebarNavProps) {
  const { docPages } = props;

  const pathname = usePathname();

  const groups = docPages
    .filter((page) => page.slug.length === 1)
    .sort((a, b) => a.order - b.order);

  return (
    <SidebarMenu className="gap-4">
      {groups.map((group) => (
        <SidebarMenuItem key={group.pathname}>
          <SidebarMenuButton
            asChild
            isActive={pathname === group.pathname}
            className="font-bold data-[active=true]:font-bold"
          >
            <Link href={group.pathname}>{group.title}</Link>
          </SidebarMenuButton>

          <RecursiveSubMenuItem slug={group.slug} docPages={docPages} pathname={pathname} />
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
}

interface RecursiveSubMenuItemProps {
  slug: string[];
  docPages: PageMetadata[];
  pathname: string;
  showNodeIcon?: boolean;
}

export function RecursiveSubMenuItem(props: RecursiveSubMenuItemProps) {
  const { slug, docPages, pathname, showNodeIcon = false } = props;

  const { setOpenMobile } = useSidebar();

  const subPages = docPages
    .filter(
      (page) =>
        page.slug.join('/').startsWith(slug.join('/')) && page.slug.length === slug.length + 1,
    )
    .sort((a, b) => a.order - b.order);

  // The recursive base case, if there are no subpages, return null
  if (subPages.length === 0) {
    return null;
  }

  return (
    <SidebarMenuSub className="mr-0 pr-0 gap-0">
      {subPages.map((subPage) => {
        const path = `/${subPage.slug.join('/')}`;

        const subSubPages = docPages
          .filter(
            (page) =>
              page.slug.join('/').startsWith(subPage.slug.join('/')) &&
              page.slug.length === subPage.slug.length + 1,
          )
          .sort((a, b) => a.order - b.order);

        return (
          <SidebarMenuItem
            key={subPage.filePath}
            style={{
              // If there are no children in the collapsible, radix doesn't set the height css variable.
              // This means that the value will be inherited from the collapse above, resulting in an ugly animation on open.
              // This fixes it by defaulting the height to 0, which radix then will override if it has an actual height.
              '--radix-collapsible-content-height': 0,
            }}
          >
            <SubItemCollapsible
              path={path}
              className="group/collapsible [&[data-state=open]>a>svg]:rotate-90"
            >
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === `/${subPage.slug.join('/')}`}
                  className="py-1"
                >
                  <Link
                    href={`/${subPage.slug.join('/')}`}
                    className="flex flex-row items-center gap-1"
                    onClick={() => {
                      // If there is no sub pages, close the mobile sidebar
                      if (subSubPages.length === 0) {
                        setOpenMobile(false);
                      }
                    }}
                  >
                    {showNodeIcon && (
                      <span className="w-5 h-5">
                        <NodeIcon size={20} name={subPage.nodeIcon ?? ''} />
                      </span>
                    )}
                    <span className="grow">{subPage.title}</span>

                    {subPage.status === 'in-progress' && (
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge className="border-border px-1 text-orange-400">
                            <Hammer weight="bold" />
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent align="end">In progress</TooltipContent>
                      </Tooltip>
                    )}
                    {subPage.status === 'placeholder' && (
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge className="border-border px-1 bg-transparent text-red-400">
                            <X weight="bold" />
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent align="end">Placeholder</TooltipContent>
                      </Tooltip>
                    )}

                    {subSubPages.length > 0 && (
                      <CaretRight weight="bold" className="transition-transform" />
                    )}
                  </Link>
                </SidebarMenuButton>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <RecursiveSubMenuItem
                  slug={subPage.slug}
                  docPages={docPages}
                  pathname={pathname}
                  showNodeIcon={subPage.sideNavIconsForSubpages}
                />
              </CollapsibleContent>
            </SubItemCollapsible>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenuSub>
  );
}

interface SubItemCollapsibleProps extends React.ComponentProps<typeof Collapsible> {
  path: string;
}

// Contains smarter logic for when to open the collapsible
function SubItemCollapsible(props: SubItemCollapsibleProps) {
  const { path, ...rest } = props;
  const pathname = usePathname();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (pathname.startsWith(path) && path.length <= pathname.length) {
      setOpen(true);
    }
  }, [pathname, path]);

  return (
    <Collapsible
      open={open}
      onOpenChange={(open) => {
        if (pathname === path) {
          // If we are on the page, allow to toggle open state
          setOpen(open);
          return;
        }

        setOpen(true); // Otherwise, always open
      }}
      {...rest}
    />
  );
}
